import React, { useCallback, useRef } from 'react';
import { Root } from '../styled';
import styled from '@emotion/styled';
import { SpacingBlock } from 'components/spacing-block';
import Footer from 'components/static-footer';
import { Hero } from './hero/Hero';
import { Divider } from './Divider';
import { BadgesSection } from './badges/BadgesSection';
import { ParametersSection } from './parameters/ParametersSection';
import { FaqSection } from './faq/FaqSection';
import { LegacyPartnersCarousel } from './carousel/LegacyPartnersCarousel';
import { NewPartnersCarousel } from './carousel/NewPartnersCarousel';
import Cover from 'screens/MadadPage/cover';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { useLocale } from 'locale';
import Breadcrumbs from 'components/breadcrumbs';
import { BreadcrumbsSection } from 'components/breadcrumbs/BreadcrumbsSection';
import { Route } from 'config/routes';
import { useSendEvent } from 'analytics/useSendEvent';
import { IntersectionSpy } from 'components/intersection-spy';

const Col = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(0, 3)};

  ${({ theme }) => theme.media.showAt({ from: 3 })`
    padding: 0;
  `}
`;

const CoverWrapper = styled.div<{ isMobile: boolean }>`
  ${({ theme, isMobile }) => isMobile ? `
    margin: ${theme.spacing(6)} -${theme.spacing(3)};
  ` : `
    margin: ${theme.spacing(8, 0, 11)};
  `}
`;

interface MadadLandingPageProps {
  heroRef: React.RefObject<HTMLDivElement>;
}

const SECTION_INTERSECTION_OPTIONS = { threshold: 0.25 };

export const MadadLandingPage: React.FC<MadadLandingPageProps> = (props) => {
  const { heroRef } = props;
  const isMobile = useScreenBreakpoint() < 3;
  const { t } = useLocale();
  const sendEvent = useSendEvent();
  const firedSections = useRef<Set<string>>(new Set());

  const handleSectionIntersection = useCallback((entry: IntersectionObserverEntry, componentId: string) => {
    if (entry.isIntersecting && !firedSections.current.has(componentId)) {
      firedSections.current.add(componentId);
      sendEvent('madad_scroll_to_section', 'madad', {
        event: { section_type: 'page_section', component_id: componentId },
      });
    }
  }, [ sendEvent ]);

  const handleBreadcrumbClick = useCallback((label: string, position: number) => {
    sendEvent('madad_breadcrumb_click', 'madad', {
      event: { clicked_label: label, position },
    });
  }, [ sendEvent ]);

  const breadcrumbItems = React.useMemo(() => {
    return [ {
      label: t('madad.navigation.breadcrumbs.home'),
      routeName: Route.Home,
    }, {
      label: t('madad.navigation.breadcrumbs.madad'),
      routeName: Route.MadadPage,
      isCurrent: true,
    } ];
  }, [ t ]);

  return (
    <div>
      <Root>
        <Col>
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_hero_search')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <Hero heroRef={heroRef} />
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_cover_landing')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <CoverWrapper isMobile={isMobile}>
            <Cover isMobile={isMobile} city="landing" source="madad_agent_picture_landing_page" />
          </CoverWrapper>
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_legacy_carousel')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <LegacyPartnersCarousel />
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_badge_info')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <BadgesSection />
          <Divider />
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_newcomers_carousel')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <NewPartnersCarousel />
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_methodology')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <ParametersSection />
          <Divider />
          <IntersectionSpy onIntersection={(e) => handleSectionIntersection(e, 'madad_faq')} intersectionOptions={SECTION_INTERSECTION_OPTIONS} />
          <FaqSection />
        </Col>
      </Root>
      <SpacingBlock mTop={15}>
        <Footer
          noBorderTop
          breadcrumbs={
            <BreadcrumbsSection>
              <Breadcrumbs items={breadcrumbItems} isFooter onItemClick={handleBreadcrumbClick} />
            </BreadcrumbsSection>
          }
        />
      </SpacingBlock>
    </div>
  );
};
